<script>
    /**
     * SPDX-FileCopyrightText: 2012-2026 Open Assessment Technologies S.A.
     * Copyright (C) 2026 (original work) Open Assessment Technologies S.A.
     *
     * SPDX-License-Identifier: AGPL-3.0-only OR LicenseRef-TAO-Commercial-License
     */
    import { onMount } from 'svelte';
    import { scopeStyles, convertPxToRem, cloneCssRules } from './stylesheets.js';
    import { applyStyleEditorTheme } from './styleEditorTheme.js';
    import { applyA11yToCustomStyles, injectA11yContentOverrideStyle } from './a11yCustomStyles.js';

    /**
     * StylesheetsLoader - non-visual component which renders temporary <link> and permanent <style/> tags.
     *
     * The downloaded stylesheets are not applied until after they are processed and reach the <style/> tag,
     * thanks to <link media="none"> attribute (which works by convention, not by specification).
     * @see https://stackoverflow.com/a/40314216
     */
    let {
        /**
         * @typedef {Object} StylesheetMeta
         * @property {String} href
         * @property {String} state
         * @property {String} scope
         * @property {String} dataSource - can be anything, renders as attribute for debugging
         * @property {CSSRuleList} cssRules
         */
        /**
         * @property {StylesheetMeta[]} stylesheets
         */
        stylesheets: stylesheetsProp,

        itemScopeSelector = '.qti-item',

        /**
         * @typedef {Object} ConvertPxToRemOptions
         * @property {Boolean?} enabled - convert or not
         * @property {String[]?} cssProperties - `[font-size]`, for example. If undefined, all css properties will be converted
         */
        /**
         * To support a11y plugin's page-zoom, convert 'px' values to 'rem'
         * @type {ConvertPxToRemOptions}
         */
        convertPxToRemOptions,

        // after all stylesheets handled
        oncomplete
    } = $props();

    // 'stylesheets' from props is not a svelte proxy, so it won't re-render when `stylesheets[i].state` is updated.
    // svelte-ignore state_referenced_locally
    let stylesheets = $state(stylesheetsProp ? [...stylesheetsProp] : []);

    const stylesheetStates = Object.freeze({
        init: 'init',
        loaded: 'loaded',
        error: 'error'
    });

    for (const sheet of stylesheets) {
        sheet.state = stylesheetStates.init;
    }

    let completedCount = $state(0);

    let stylesContainerElt;

    /** @type {Set<String>} */
    // eslint-disable-next-line svelte/prefer-svelte-reactivity
    const injectedA11yOverrideScopes = new Set();

    /**
     * Inject conditional a11y override rules once per scope selector.
     * @param {String} scopeSelector
     */
    function injectA11yOverrideForScope(scopeSelector) {
        if (!scopeSelector || injectedA11yOverrideScopes.has(scopeSelector)) {
            return;
        }

        injectA11yContentOverrideStyle(stylesContainerElt, scopeSelector);
        injectedA11yOverrideScopes.add(scopeSelector);
    }

    /**
     * Scope stylesheet rules, and inject into a <style> tag
     * TODO: in future, receive the parameters for 'scopeStyles()' from itemRunnerConfig
     * @param {StylesheetMeta} stylesheet
     */
    function injectScopedStyleTag(stylesheet) {
        /**
         * @type {CSSRuleList}
         * @see https://developer.mozilla.org/en-US/docs/Web/API/CSSRuleList
         */
        let { cssRules } = stylesheet || {};
        const clonedCssRules = cloneCssRules(cssRules);
        if (clonedCssRules) {
            cssRules = clonedCssRules;
        }

        const newCssRules = applyStyleEditorTheme(cssRules);
        if (newCssRules) {
            cssRules = newCssRules;
        }

        const scopeSelector = stylesheet.scope || itemScopeSelector;

        // Convert px to rem before a11y scaling so both page-zoom (rem) and font-size scale apply to lengths.
        if (convertPxToRemOptions && convertPxToRemOptions.enabled) {
            convertPxToRem(cssRules, convertPxToRemOptions.cssProperties);
        }

        applyA11yToCustomStyles(cssRules);

        // prefix rules
        const toReplace = ['body div.qti-item', '.qti-item']; // support legacy editor styles and modern editor styles (order matters too!)
        const scopedCssRules = scopeStyles(cssRules, scopeSelector, toReplace);

        // prepare & inject new style tag
        const styleTag = document.createElement('style');
        styleTag.innerHTML = scopedCssRules;
        styleTag.dataset.scoped = true;
        if (stylesheet.dataSource) {
            styleTag.dataset.source = stylesheet.dataSource;
        }
        if (stylesheet.href) {
            styleTag.dataset.stylesheetHref = stylesheet.href;
        }
        // eslint-disable-next-line svelte/no-dom-manipulating
        stylesContainerElt.appendChild(styleTag);

        injectA11yOverrideForScope(scopeSelector);

        // update state in sheets, to clean up <head> template
        stylesheet.state = stylesheetStates.loaded;
        completedCount++;
    }

    /**
     * Process a loaded stylesheet resource into scoped CSS and inject into a <style> tag.
     * @param {Event} e
     * @param {StylesheetMeta} stylesheet
     */
    function handleStylesheetLoad(e, stylesheet) {
        // get cssRules from owner link tag, referenced in load event
        const path = e && e.composedPath && e.composedPath();
        const linkTag = path[0];
        stylesheet.cssRules = linkTag?.sheet?.cssRules;

        injectScopedStyleTag(stylesheet);
    }

    /**
     * Mark a stylesheet as errored
     * @param {Event} e
     * @param {StylesheetMeta} stylesheet
     */
    function handleStylesheetError(e, stylesheet) {
        stylesheet.state = stylesheetStates.error;
        completedCount++;
    }

    // announce when all resources settled
    $effect(() => {
        if (completedCount === stylesheets.length) {
            oncomplete?.();
        }
    });

    onMount(() => {
        // process non-href stylesheets without using <link> part of template
        stylesheets
            .filter(ss => !ss.href)
            .forEach(ss => {
                injectScopedStyleTag(ss);
            });
    });
</script>

<svelte:head>
    {#each stylesheets as ss (ss)}
        {#if ss.href && ss.state === stylesheetStates.init}
            <link
                crossorigin="anonymous"
                rel="stylesheet"
                media="none"
                href={ss.href}
                onload={e => handleStylesheetLoad(e, ss)}
                onerror={e => handleStylesheetError(e, ss)} />
        {/if}
    {/each}
</svelte:head>

<div class="styles-container" bind:this={stylesContainerElt}></div>
